import React, { useState, useEffect } from "react"
import {
  Card,
  Col,
  Container,
  Input,
  Label,
  Row,
  Form,
  Button,
  CardBody,
} from "reactstrap"
import Select from "react-select"
import makeAnimated from "react-select/animated"
import * as Yup from "yup"
import { useFormik, FieldArray } from "formik"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { useNavigate, useParams } from "react-router-dom"
import { stateOptions } from "../config/globalConfig"

const animatedComponents = makeAnimated()
import Loader from "./Common/Loader"
import ErrorPopup from "./ErrorPopup"
import { useDispatch, useSelector } from "react-redux"
import {
  createEventBookingOrder,
  verifyEventPayment,
} from "../redux/actions/event"
import {
  CREATE_EVENT_BOOKING_ORDER_RESET,
  VERIFY_EVENT_PAYMENT_RESET,
} from "../redux/types/event"
import PageLoader from "./Common/PageLoader"

const optionFoodPreference = [
  {
    options: [
      { label: "Veg", value: "veg" },
      { label: "Non-Veg", value: "non-veg" },
      { label: "Jain", value: "jain" },
    ],
  },
]
const optionTshirtSize = [
  {
    options: [
      { label: "XS – 36", value: "XS – 36" },
      { label: "S – 38", value: "S – 38" },
      { label: "M – 40", value: "M – 40" },
      { label: "L – 42", value: "L – 42" },
      { label: "XL – 44", value: "XL – 44" },
      { label: "XXL – 46", value: "XXL – 46" },
      { label: "3XL – 48", value: "3XL – 48" },
      { label: "4XL – 50", value: "4XL – 50" },
    ],
  },
]

const optionSalutation = [
  {
    options: [
      { label: "Ms.", value: "ms" },
      { label: "Mrs.", value: "mrs" },
      { label: "Dr.", value: "dr" },
    ],
  },
]

const EventAnniversary = () => {
  // Use useParams to get the event URL slug
  const { url } = useParams()
  const history = useNavigate()
  const dispatch = useDispatch()

  const [payementLoader, setPaymentLoader] = useState(false)
  const [showCoupon, setShowCoupon] = useState(false)
  const [appliedCoupon, setAppliedCoupon] = useState(null)
  const [couponCode, setCouponCode] = useState(null)
  const [appliedCouponError, setAppliedCouponError] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [selectedPvsCard, setSelectedPvsCard] = useState(null)
  const [pvsOptions, setPvsOptions] = useState([])
  const [ticketOptions, setTicketOptions] = useState([])

  const [showFoodAndTshirt, setShowFoodAndTshirt] = useState(true)
  const [eventData, setEventData] = useState()
  const [guests, setGuests] = useState([])
  const [tickets, setTickets] = useState([])
  const [calculations, setCalculations] = useState({
    subtotal: 0,
    gst: 0,
    total: 0,
    grandTotal: 0,
    payableAmount: 0,
    discountAmount: 0,
  })

  const pvs_cards = useSelector(state => state.auth.pvs_cards)
  const createEventBookingOrderScreen = useSelector(
    state => state.screens.event.createEventBookingOrder
  )
  const verifyEventPaymentScreen = useSelector(
    state => state.screens.event.verifyEventPayment
  )
  const bookingOrderDetails = useSelector(
    state => state.event.bookingOrderDetails
  )

  useEffect(() => {
    console.log("createEventBookingOrderScreen", createEventBookingOrderScreen)

    if (createEventBookingOrderScreen.success) {
      const orderDetails = bookingOrderDetails || {}
      const razorpayOrder = orderDetails.razorpay_order
      if (calculations.payableAmount === 0 && razorpayOrder === null) {
        history("/congratulations", { replace: true, state: { eventData } })
      } else if (razorpayOrder && razorpayOrder.id && razorpayOrder.amount) {
        handleRazorpayPayment(orderDetails)
      } else {
        setPaymentLoader(false)
        alert("Could not initiate payment. Please try again.")
      }
      dispatch({ type: CREATE_EVENT_BOOKING_ORDER_RESET })
    }
    if (createEventBookingOrderScreen.error) {
      setPaymentLoader(false)
      toast.error(createEventBookingOrderScreen.error)
      dispatch({ type: CREATE_EVENT_BOOKING_ORDER_RESET })
    }
  }, [createEventBookingOrderScreen])

  useEffect(() => {
    if (verifyEventPaymentScreen.success) {
      dispatch({ type: VERIFY_EVENT_PAYMENT_RESET })
      setPaymentLoader(false)
      toast.success("🎉 Payment successful!")
      history("/congratulations", {
        replace: true,
        state: { eventData },
      })
    }
    if (verifyEventPaymentScreen.error) {
      setPaymentLoader(false)
      setErrorMessage(verifyEventPaymentScreen.error)
      setIsOpen(true)
      dispatch({ type: VERIFY_EVENT_PAYMENT_RESET })
    }
  }, [verifyEventPaymentScreen])

  useEffect(() => {
    async function fetchCategories() {
      try {
        // Use the slug in the API endpoint
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/dashboard/get-event-details/${url}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        )

        const data = await response.json()
        setEventData(data.event)
      } catch (err) {
        setOptionCategory([{ options: [] }])
        setEventData({})
      }
    }
    fetchCategories()
  }, [url]) // Add url as dependency

  useEffect(() => {
    if (eventData && eventData.event_type === "netwoking-meet") {
      setShowFoodAndTshirt(false)
    }
    if (eventData && eventData.tickets && eventData.tickets.length > 0) {
      const ticketOptions = eventData.tickets.map(ticket => ({
        label:
          ticket.title + (ticket.sold >= ticket.seats ? " (Sold Out)" : ""),
        value: ticket.id,
        cost: ticket.cost,
        is_child: ticket.is_child,
        seats: ticket.seats,
        sold: ticket.sold,
        isDisabled: ticket.sold >= ticket.seats, // Disable if sold out
      }))
      setTicketOptions([{ options: ticketOptions }])
    }
  }, [eventData])

  useEffect(() => {
    let tempCalculations = { ...calculations }
    tempCalculations.subtotal = tickets.reduce(
      (sum, ticket) => sum + ticket.cost * ticket.count,
      0
    )
    if (appliedCoupon) {
      tempCalculations.discountAmount =
        tempCalculations.subtotal *
        (Number(appliedCoupon.discount_percent) / 100)
      tempCalculations.subtotal =
        tempCalculations.subtotal - tempCalculations.discountAmount
    }
    tempCalculations.gst = tempCalculations.subtotal * 0.18
    tempCalculations.payableAmount =
      tempCalculations.subtotal + tempCalculations.gst
    console.log("tempCalculations", tempCalculations)

    setCalculations(tempCalculations)
  }, [tickets, appliedCoupon])

  useEffect(() => {
    if (pvs_cards && pvs_cards.length > 0) {
      let tempOptions = []
      tempOptions = pvs_cards.map(card => ({
        ...card,
        label: card.token_code,
        value: card.token_code,
      }))
      setPvsOptions(tempOptions)
      setSelectedPvsCard(tempOptions[0])
    }
  }, [pvs_cards])

  const handlePvsCardChange = selectedOption => {
    setSelectedPvsCard(selectedOption)
  }

  const toggleCoupon = () => {
    setShowCoupon(prev => !prev)
  }

  const updateTicketsForGuest = guest => {
    const selectedTicket = guest?.ticket
    if (!selectedTicket?.value) {
      return
    }
    setTickets(prevTickets => {
      const existingTicket = prevTickets.find(
        ticket => ticket.id === selectedTicket.value
      )
      if (existingTicket) {
        return prevTickets.map(ticket =>
          ticket.id === selectedTicket.value
            ? { ...ticket, count: ticket.count + 1 }
            : ticket
        )
      }
      return [
        ...prevTickets,
        {
          ticket: selectedTicket.label,
          id: selectedTicket.value,
          cost: selectedTicket.cost ?? 0,
          count: 1,
        },
      ]
    })
  }

  const deleteGuestRow = idx => {
    // Remove guest at index idx
    const updatedGuests = guests.filter((_, i) => i !== idx)
    setGuests(updatedGuests)
  }

  const handleAddGuest = async () => {
    const errors = await guestValidation.validateForm()
    console.log("errors...", errors)

    if (Object.keys(errors).length > 0) {
      guestValidation.setTouched(
        {
          salutation: true,
          full_name: true,
          whatsapp_number: true,
          food_pref: true,
          tshirt_size: true,
          ticket: true,
        },
        true
      )
      return
    }

    const newGuest = {
      salutation: guestValidation.values.salutation,
      full_name: guestValidation.values.full_name,
      whatsapp_number: guestValidation.values.whatsapp_number,
      food_pref: guestValidation.values.food_pref,
      tshirt_size: guestValidation.values.tshirt_size,
      ticket: guestValidation.values.ticket,
    }
    setGuests(prevGuests => [...prevGuests, newGuest])
    updateTicketsForGuest(newGuest)
    guestValidation.resetForm()
  }

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      salutation: null,
      firstName: "",
      lastName: "",
      companyName: "",
      address: "",
      city: "Pune",
      state: stateOptions[0].options[20],
      pincode: "",
      gstNumber: "",
      email: "",
      mobileNo: "",
    },
    validationSchema: Yup.object({
      salutation: Yup.object().nullable().required("Salutation is required"),
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      companyName: Yup.string().required("Company name is required"),
      address: Yup.string().required("Address is required"),
      city: Yup.string().required("City is required"),
      state: Yup.object().nullable().required("State is required"),
      pincode: Yup.string()
        .matches(/^[0-9]{6}$/, "Pincode must be 6 digits")
        .required("Pincode is required"),
      gstNumber: Yup.string()
        .matches(
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
          "Invalid GST number format"
        )
        .nullable(),
      email: Yup.string()
        .email("Invalid email format")
        .required("Email is required"),
      mobileNo: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
        .required("Mobile number is required"),
    }),

    onSubmit: async values => {
      // console.log("values...", values)
      // console.log("guests...", guests)
      // console.log("tickets...", tickets)
      if (guests.length > 0) {
        let guestTickets = []
        if (eventData.event_type === "netwoking-meet") {
          guestTickets.push({
            ticket_id: guests[0].ticket.value,
            title: guests[0].ticket.label,
            pvs_card_number: selectedPvsCard.token_code,
            business_name: values.companyName,
            salutation: values.salutation.label,
            full_name: values.firstName + " " + values.lastName,
            phone: values.mobileNo,
            wa_contact: values.mobileNo,
            food_preferences: null,
            tshirt_size: null,
            total: 1,
          })
        } else {
          guests.map(guest => {
            guestTickets.push({
              ticket_id: guest.ticket.value,
              title: guest.ticket.label,
              pvs_card_number: selectedPvsCard.token_code,
              business_name: values.companyName,
              salutation: guest.salutation.label,
              full_name: guest.full_name,
              phone: guest.whatsapp_number,
              wa_contact: guest.whatsapp_number,
              food_preferences: guest.food_pref.label,
              tshirt_size: guest.tshirt_size.label,
              total: 1,
            })
          })
        }

        let requestData = {
          buyer: {
            pvs_card_number: selectedPvsCard.token_code,
            wa_contact: values.mobileNo,
            salutation: values.salutation.label,

            business_name: values.companyName,
            full_name: values.firstName + " " + values.lastName,
            email: values.email,
            phone: values.mobileNo,

            address: values.address,
            city: values.city,
            state: values.state.label,
            pincode: values.pincode,
            gst_number: values.gstNumber,
          },
          currency: "INR",
          discounted_amount: calculations.payableAmount,
          event_type: eventData.event_type,
          sub_total: calculations.subtotal,
          tax: calculations.gst,
          tickets: guestTickets,
          total: calculations.payableAmount,
          total_tickets: tickets.reduce((sum, ticket) => sum + ticket.count, 0),
          url: eventData.url,
          used_coupon_id: appliedCoupon?.coupon_id,
          used_discount_percent: appliedCoupon?.discount_percent,
        }
        console.log("requestData...", requestData)

        dispatch(createEventBookingOrder(requestData))
      } else {
        setErrorMessage("Please add at least one guest")
        setIsOpen(true)
      }
    },
  })

  const guestValidation = useFormik({
    initialValues: {
      salutation: null,
      full_name: "",
      whatsapp_number: "",
      food_pref: null,
      tshirt_size: null,
      ticket: null,
    },
    validationSchema: Yup.object({
      salutation: Yup.object().nullable().required("Salutation required"),
      full_name: Yup.string().max(100).required("Full name required"),
      whatsapp_number: Yup.string()
        .matches(/^[0-9]{10}$/, "10 digits")
        .required("WhatsApp required"),
      food_pref: Yup.object()
        .nullable()
        .when([], {
          is: () => showFoodAndTshirt,
          then: schema => schema.required("Food required"),
          otherwise: schema => schema.nullable(),
        }),
      tshirt_size: Yup.object()
        .nullable()
        .when([], {
          is: () => showFoodAndTshirt,
          then: schema => schema.required("T-shirt required"),
          otherwise: schema => schema.nullable(),
        }),
      ticket: Yup.object().nullable().required("Ticket required"),
    }),
    onSubmit: () => {},
  })

  useEffect(() => {
    if (selectedPvsCard && selectedPvsCard.billing) {
      validation.setValues({
        category: "",
        salutation: null,
        firstName: selectedPvsCard.billing.first_name || "",
        lastName: selectedPvsCard.billing.last_name || "",
        companyName: selectedPvsCard.billing.company_name || "",
        address: selectedPvsCard.billing.address || "",
        city: selectedPvsCard.billing.city || "Pune",
        state: selectedPvsCard.billing.state
          ? stateOptions[0].options.find(
              val => val.label === selectedPvsCard.billing.state
            )
          : stateOptions[0].options[20],
        pincode: selectedPvsCard.billing.pincode || "",
        gstNumber: selectedPvsCard.billing.gst_number || "",
        email: selectedPvsCard.billing.email_id || "",
        mobileNo: selectedPvsCard.billing.mobile_no || "",
      })
      if (ticketOptions.length > 0) {
        if (eventData.event_type === "netwoking-meet") {
          // Add initial guest entry with PVS card holder details
          console.log(
            "ticketOptions[0].options[0].value",
            ticketOptions[0].options[0]
          )

          const initialGuest = {
            salutation: null,
            full_name: `${selectedPvsCard.billing.first_name || ""} ${
              selectedPvsCard.billing.last_name || ""
            }`.trim(),
            whatsapp_number: selectedPvsCard.billing.mobile_no || "",
            food_pref: null,
            tshirt_size: null,
            ticket: ticketOptions[0].options[0],
            pvs_card_number: selectedPvsCard.token_code || "",
            business_name: selectedPvsCard.billing.company_name || "",
          }

          setGuests([initialGuest])
          setTickets([
            {
              ticket: initialGuest.ticket?.label,
              id: initialGuest.ticket?.value,
              cost: initialGuest.ticket?.cost ?? 0,
              count: 1,
            },
          ])
        } else {
          guestValidation.setValues({
            salutation: null,
            full_name: `${selectedPvsCard.billing.first_name || ""} ${
              selectedPvsCard.billing.last_name || ""
            }`.trim(),
            whatsapp_number: selectedPvsCard.billing.mobile_no || "",
            food_pref: null,
            tshirt_size: null,
            ticket: null,
          })
        }
      }
    }
  }, [selectedPvsCard, ticketOptions])

  const handleRazorpayPayment = orderDetails => {
    const razorpayOrder = orderDetails.razorpay_order
    setPaymentLoader(true)
    const options = {
      key: process.env.REACT_APP_RZRPAY_KEY,
      amount: razorpayOrder.amount,
      currency: razorpayOrder.currency || "INR",
      name: "Event Payment",
      description: "Event booking payment",
      order_id: razorpayOrder.id,
      receipt: razorpayOrder.receipt,
      handler: function (response) {
        dispatch(
          verifyEventPayment({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature,
            booking_id: orderDetails.booking_id,
            subtotal: calculations.subtotal,
            tax: calculations.gst,
            total: calculations.payableAmount,
            currency: "INR",
          })
        )
      },
      modal: {
        ondismiss: () => {
          setPaymentLoader(false)
          toast.info("Payment cancelled.")
        },
      },
      prefill: {
        email: validation.values.email || "",
      },
      theme: {
        color: "#3399cc",
      },
    }
    if (window.Razorpay) {
      const rzp = new window.Razorpay(options)
      rzp.on("payment.failed", err => {
        setPaymentLoader(false)
        console.error("Payment failed:", err.error.description)
        toast.error("❌ Payment failed. Please try again.")
      })
      rzp.on("payment.error", () => {
        setPaymentLoader(false)
        toast.error("Payment could not be completed.")
      })
      rzp.open()
    } else {
      setPaymentLoader(false)
      alert(
        "Payment gateway not loaded. Please refresh the page and try again."
      )
    }
  }

  const applyCoupon = async () => {
    console.log(couponCode)
    if (couponCode === null || (couponCode && couponCode.trim() === "")) {
      setAppliedCouponError("Please enter a coupon code")
      return
    }
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/events/apply-coupon`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            coupon: couponCode,
          }),
        }
      )
      const data = await res.json()
      console.log("api response ", data)

      if (data.success) {
        setAppliedCoupon({
          coupon_id: data.coupon_id,
          coupon: data.coupon,
          discount_percent: data.discount_percent,
        })
      } else {
        setAppliedCouponError(data.message)
      }
    } catch (err) {
      console.error(err)
      setAppliedCouponError("Failed to apply coupon")
    }
  }

  const removeCoupon = () => {
    setAppliedCoupon(null)
    setAppliedCouponError(null)
  }

  useEffect(() => {
    console.log("guests", guests)
  }, [guests])

  return (
    <>
      <section id="event-form" className="pb-4">
        <Container fluid={true}>
          <Form
            className="mt-4"
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
              return false
            }}
            action="#"
          >
            <Row>
              {/* Left side --------------------------------------------------- */}
              <Col xs={12} md={12} lg={8}>
                <div className="bookevent-section">
                  <Card className="mb-4">
                    <CardBody>
                      <Row>
                        <Col md="12">
                          <div className="mb-3">
                            <Label htmlFor="pvsCards" className="form-label">
                              Select PVS Card{" "}
                              <span className="text-danger">*</span>
                            </Label>
                            <Select
                              id="pvsCards"
                              name="pvsCards"
                              options={pvsOptions}
                              classNamePrefix="select"
                              placeholder="Select your PVS Card..."
                              value={selectedPvsCard}
                              onChange={handlePvsCardChange}
                            />
                            {validation.touched.selectedPvsCard &&
                              validation.errors.selectedPvsCard && (
                                <div className="text-danger small mt-1">
                                  {validation.errors.selectedPvsCard}
                                </div>
                              )}
                          </div>
                        </Col>
                      </Row>

                      <h5 className="nom-title mt-4">Billing Details</h5>

                      {/* Primary Contact */}
                      <div className="mb-3 row mt-3">
                        <div className="col-12 col-sm-3">
                          <Label
                            className="form-label"
                            htmlFor="primary_contact"
                          >
                            Primary Contact &nbsp;
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        <div className="col-12 col-sm">
                          <div className="row">
                            <div className="col-12 col-sm-3 mb-3">
                              <Select
                                name="salutation"
                                value={validation.values.salutation}
                                onChange={option =>
                                  validation.setFieldValue("salutation", option)
                                }
                                onBlur={() =>
                                  validation.setFieldTouched("salutation", true)
                                }
                                options={optionSalutation}
                                components={animatedComponents}
                                classNamePrefix="select2-selection"
                                className={
                                  validation.touched.salutation &&
                                  validation.errors.salutation
                                    ? "is-invalid"
                                    : ""
                                }
                              />
                              {validation.touched.salutation &&
                                validation.errors.salutation && (
                                  <div className="invalid-feedback d-block">
                                    {validation.errors.salutation}
                                  </div>
                                )}
                            </div>
                            <div className="col-12 col-sm-4 mb-3">
                              <Input
                                name="firstName"
                                className="form-control"
                                placeholder="First Name"
                                type="text"
                                id="first_name"
                                value={validation.values.firstName}
                                onChange={validation.handleChange}
                                // onBlur={validation.handleBlur}
                              />
                              {validation.errors.firstName && (
                                <div className="text-danger small mt-1">
                                  {validation.errors.firstName}
                                </div>
                              )}
                            </div>
                            <div className="col-12 col-sm-5">
                              <Input
                                name="lastName"
                                className="form-control"
                                placeholder="Last Name"
                                id="last_name"
                                type="text"
                                value={validation.values.lastName}
                                onChange={validation.handleChange}
                                // onBlur={validation.handleBlur}
                              />
                              {validation.errors.lastName && (
                                <div className="text-danger small mt-1">
                                  {validation.errors.lastName}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Contact Info */}
                      <div className="mb-3 row mt-3">
                        <div className="col-12 col-sm-3">
                          <Label className="form-label" htmlFor="contact_info">
                            Contact Info &nbsp;
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        <div className="col-12 col-sm">
                          <div className="row">
                            <div className="col-12 col-sm-6 mb-3">
                              <Input
                                name="email"
                                className="form-control"
                                type="email"
                                id="emailr"
                                value={validation.values.email}
                                onChange={validation.handleChange}
                              />
                              {validation.errors.email && (
                                <div className="text-danger small mt-1">
                                  {validation.errors.email}
                                </div>
                              )}
                            </div>
                            <div className="col-12 col-sm-6">
                              <Input
                                name="mobileNo"
                                className="form-control"
                                type="number"
                                id="Mob_number"
                                value={validation.values.mobileNo}
                                onChange={validation.handleChange}
                              />
                              {validation.errors.mobileNo && (
                                <div className="text-danger small mt-1">
                                  {validation.errors.mobileNo}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Company Info */}
                      <div className="mb-3 row">
                        <div className="col-12 col-sm-3">
                          <Label className="form-label" htmlFor="company_name">
                            Company Info &nbsp;
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        <div className="col-12 col-sm">
                          <div className="row">
                            <div className="col-12 col-sm-6 mb-3">
                              <Input
                                name="companyName"
                                className="form-control"
                                type="text"
                                id="company_name"
                                value={validation.values.companyName}
                                onChange={validation.handleChange}
                                // onBlur={validation.handleBlur}
                              />
                              {validation.errors.companyName && (
                                <div className="text-danger small mt-1">
                                  {validation.errors.companyName}
                                </div>
                              )}
                            </div>
                            <div className="col-12 col-sm-6">
                              <Input
                                name="gstNumber"
                                className="form-control"
                                type="text"
                                id="gst_number"
                                value={validation.values.gstNumber}
                                onChange={validation.handleChange}
                                // onBlur={validation.handleBlur}
                              />
                              {/* {validation.errors.gstNumber && (
                              <div className="text-danger small mt-1">
                                {validation.errors.gstNumber}
                              </div>
                            )} */}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Address */}
                      <div className="mb-3 row">
                        <div className="col-12 col-sm-3">
                          <Label className="form-label" htmlFor="address">
                            Address &nbsp;
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        <div className="col-12 col-sm-9">
                          <textarea
                            name="address"
                            className="form-control"
                            id="address"
                            rows="3"
                            value={validation.values.address}
                            onChange={validation.handleChange}
                            // onBlur={validation.handleBlur}
                          ></textarea>
                          {validation.errors.address && (
                            <div className="text-danger small mt-1">
                              {validation.errors.address}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* State, City, Pincode */}
                      <div className="mb-3 row">
                        <div className="col-12 col-sm-3">
                          <Label className="form-label" htmlFor="state_city">
                            State &amp; City &nbsp;
                            <span className="text-danger">*</span>
                          </Label>
                        </div>
                        <div className="col-12 col-sm">
                          <div className="row">
                            <div className="col-12 col-sm-4 mb-3">
                              <Select
                                name="state"
                                value={validation.values.state}
                                onChange={val => {
                                  validation.setFieldValue("state", val)
                                }}
                                options={stateOptions}
                                classNamePrefix="select2-selection"
                              />
                              {validation.errors.state && (
                                <div className="text-danger small mt-1">
                                  {validation.errors.state}
                                </div>
                              )}
                            </div>
                            <div className="col-12 col-sm-4 mb-3">
                              <Input
                                name="city"
                                className="form-control"
                                type="text"
                                id="city"
                                value={validation.values.city}
                                onChange={validation.handleChange}
                                // onBlur={validation.handleBlur}
                              />
                              {validation.errors.city && (
                                <div className="text-danger small mt-1">
                                  {validation.errors.city}
                                </div>
                              )}
                            </div>
                            <div className="col-12 col-sm-4">
                              <Input
                                name="pincode"
                                className="form-control"
                                type="number"
                                id="pincode"
                                value={validation.values.pincode}
                                onChange={validation.handleChange}
                                // onBlur={validation.handleBlur}
                              />
                              {validation.errors.pincode && (
                                <div className="text-danger small mt-1">
                                  {validation.errors.pincode}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>

                  <Card className="p-4" hidden={!showFoodAndTshirt}>
                    <div className="bookevent-card">
                      <div>
                        <Row className="align-items-center justify-content-between border-bottom mb-4 pb-3">
                          <Col>
                            <h2 className=" font-size-20 font-weight-bold">
                              Guest Details
                            </h2>
                          </Col>
                          <Col className="d-flex justify-content-end">
                            {/* Add Guest button — only for anniversary */}
                            {showFoodAndTshirt && (
                              <Button
                                type="button"
                                color="success"
                                onClick={handleAddGuest}
                                className="mt-2"
                                style={{ minWidth: 120 }}
                              >
                                + Add Guest
                              </Button>
                            )}
                          </Col>
                        </Row>
                        <Card className="mb-4 p-3">
                          {/* Name, Salutation, Phone, PVS Card */}
                          <Row>
                            <Col xs={12} sm={4} md={3} ld={2}>
                              <div className="mb-3">
                                <Label className="eann-label">
                                  Salutation
                                  <span className="asterisk-icon">*</span>
                                </Label>
                                <Select
                                  name="salutation"
                                  value={
                                    optionSalutation[0].options.find(
                                      o =>
                                        o.value ===
                                        guestValidation.values.salutation?.value
                                    ) || null
                                  }
                                  onChange={option =>
                                    guestValidation.setFieldValue(
                                      "salutation",
                                      option
                                    )
                                  }
                                  onBlur={() =>
                                    guestValidation.setFieldTouched(
                                      "salutation",
                                      true
                                    )
                                  }
                                  options={optionSalutation}
                                  components={animatedComponents}
                                  classNamePrefix="select2-selection"
                                  className={
                                    guestValidation.touched.salutation &&
                                    guestValidation.errors.salutation
                                      ? "is-invalid"
                                      : ""
                                  }
                                />
                                {guestValidation.touched.salutation &&
                                  guestValidation.errors.salutation &&
                                  guestValidation.submitCount > 0 && (
                                    <div className="invalid-feedback d-block">
                                      {guestValidation.errors.salutation}
                                    </div>
                                  )}
                              </div>
                            </Col>

                            <Col xs={12} sm={8} md={5} lg={5}>
                              <div className="mb-3">
                                <Label className="form-label">
                                  Full Name
                                  <span className="asterisk-icon">*</span>
                                </Label>
                                <Input
                                  name="full_name"
                                  type="text"
                                  className={`form-control ${
                                    guestValidation.touched.full_name &&
                                    guestValidation.errors.full_name
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  value={guestValidation.values.full_name}
                                  onChange={guestValidation.handleChange}
                                  onBlur={guestValidation.handleBlur}
                                  placeholder="Enter Full Name"
                                />
                                {guestValidation.touched.full_name &&
                                  guestValidation.errors.full_name && (
                                    <div className="invalid-feedback d-block">
                                      {guestValidation.errors.full_name}
                                    </div>
                                  )}
                              </div>
                            </Col>

                            <Col xs={12} md={4} lg={4}>
                              <div className="mb-3">
                                <Label className="form-label">
                                  WhatsApp Number
                                  <span className="asterisk-icon">*</span>
                                </Label>
                                <Input
                                  name="whatsapp_number"
                                  type="text"
                                  className={`form-control ${
                                    guestValidation.touched.whatsapp_number &&
                                    guestValidation.errors.whatsapp_number
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  value={guestValidation.values.whatsapp_number}
                                  onChange={guestValidation.handleChange}
                                  onBlur={guestValidation.handleBlur}
                                  placeholder="Enter 10 Digit No"
                                />
                                {guestValidation.touched.whatsapp_number &&
                                  guestValidation.errors.whatsapp_number && (
                                    <div className="invalid-feedback d-block">
                                      {guestValidation.errors.whatsapp_number}
                                    </div>
                                  )}
                              </div>
                            </Col>
                          </Row>

                          {/* Food note | only for anniversary */}

                          {showFoodAndTshirt && (
                            <Row>
                              <Col sm={12} md={showFoodAndTshirt ? 12 : 4}>
                                <div className="mb-3">
                                  <Label className="eann-label">
                                    Select Ticket
                                    <span className="asterisk-icon">*</span>
                                  </Label>
                                  <Select
                                    name="ticket"
                                    value={guestValidation.values.ticket}
                                    onChange={option =>
                                      guestValidation.setFieldValue(
                                        "ticket",
                                        option
                                      )
                                    }
                                    onBlur={() =>
                                      guestValidation.setFieldTouched(
                                        "ticket",
                                        true
                                      )
                                    }
                                    options={ticketOptions}
                                    classNamePrefix="select2-selection"
                                    components={animatedComponents}
                                  />
                                </div>
                              </Col>
                              <Col sm={12} md={4}>
                                <div className="mb-3">
                                  <Label className="eann-label">
                                    Food Preferences
                                    <span className="asterisk-icon">*</span>
                                  </Label>
                                  <Select
                                    name="food_pref"
                                    value={
                                      optionFoodPreference[0].options.find(
                                        o =>
                                          o.value ===
                                          guestValidation.values.food_pref
                                            ?.value
                                      ) || null
                                    }
                                    onChange={option =>
                                      guestValidation.setFieldValue(
                                        "food_pref",
                                        option
                                      )
                                    }
                                    onBlur={() =>
                                      guestValidation.setFieldTouched(
                                        "food_pref",
                                        true
                                      )
                                    }
                                    options={optionFoodPreference}
                                    classNamePrefix="select2-selection"
                                    components={animatedComponents}
                                    className={
                                      guestValidation.touched.food_pref &&
                                      guestValidation.errors.food_pref
                                        ? "is-invalid"
                                        : ""
                                    }
                                  />
                                  {guestValidation.touched.food_pref &&
                                    guestValidation.errors.food_pref &&
                                    guestValidation.submitCount > 0 && (
                                      <div className="invalid-feedback d-block">
                                        {guestValidation.errors.food_pref}
                                      </div>
                                    )}
                                </div>
                              </Col>
                              <Col sm={12} md={4}>
                                <div className="mb-3">
                                  <Label className="eann-label">
                                    T-Shirt Sizes (In inches)
                                    <span className="asterisk-icon">*</span>
                                  </Label>
                                  <Select
                                    name="tshirt_size"
                                    value={
                                      optionTshirtSize[0].options.find(
                                        o =>
                                          o.value ===
                                          guestValidation.values.tshirt_size
                                            ?.value
                                      ) || null
                                    }
                                    onChange={option =>
                                      guestValidation.setFieldValue(
                                        "tshirt_size",
                                        option
                                      )
                                    }
                                    onBlur={() =>
                                      guestValidation.setFieldTouched(
                                        "tshirt_size",
                                        true
                                      )
                                    }
                                    options={optionTshirtSize}
                                    classNamePrefix="select2-selection"
                                    components={animatedComponents}
                                    className={
                                      guestValidation.touched.tshirt_size &&
                                      guestValidation.errors.tshirt_size
                                        ? "is-invalid"
                                        : ""
                                    }
                                  />
                                  {guestValidation.touched.tshirt_size &&
                                    guestValidation.errors.tshirt_size &&
                                    guestValidation.submitCount > 0 && (
                                      <div className="invalid-feedback d-block">
                                        {guestValidation.errors.tshirt_size}
                                      </div>
                                    )}
                                </div>
                              </Col>
                            </Row>
                          )}

                          {/* Tshirt note | only for anniversary */}
                          {showFoodAndTshirt && (
                            <p className="mb-0">
                              Eg - A 40 chest size lady should select L size, as
                              it would not be tight and fall on the body. If you
                              wear bodyfit, then select as per your chest size.
                              <br />❌ Once selected, no changes are possible.
                              <br />❌ No exchange available at the venue.
                            </p>
                          )}
                        </Card>
                        {guests.length > 0 && (
                          <div className="table-responsive">
                            <table className="table table-bordered mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Salutation</th>
                                  <th>Name</th>
                                  <th>WhatsApp</th>
                                  <th>Ticket</th>
                                  {!showFoodAndTshirt && (
                                    <th>Food Preference</th>
                                  )}
                                  {!showFoodAndTshirt && <th>T-Shirt Size</th>}
                                  <th
                                    className="text-center"
                                    style={{ width: 60 }}
                                  >
                                    Action
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {guests.map((guest, index) => (
                                  <tr>
                                    <td>{guest.salutation?.label || "-"}</td>
                                    <td>{guest.full_name || "-"}</td>
                                    <td>{guest.whatsapp_number || "-"}</td>
                                    <td>{guest.ticket?.label || "-"}</td>
                                    {!showFoodAndTshirt && (
                                      <td>{guest.food_pref?.label || "-"}</td>
                                    )}
                                    {!showFoodAndTshirt && (
                                      <td>{guest.tshirt_size?.label || "-"}</td>
                                    )}
                                    {index > 0 && (
                                      <td
                                        className="text-center"
                                        style={{ width: 60 }}
                                      >
                                        <Button
                                          type="button"
                                          color="danger"
                                          className="ti-trash"
                                          onClick={() => deleteGuestRow(index)}
                                        />
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                </div>
              </Col>

              {/* Right side (summary) --------------------------------------- */}
              <Col xs={12} md={12} lg={4}>
                <Card className="p-4 rightform">
                  <div className="">
                    <div className="amount-box">
                      <div className="workshop-box pb-2 ">
                        <h2 className="h4">Event Details</h2>
                        <h3 className="h5">
                          {eventData?.title || "Event Title"}
                        </h3>
                      </div>
                      <div className="no-tickets pt-3 pb-2">
                        <h2 className="card-title">NUMBER OF GUEST</h2>
                        <div id="ticket-details">
                          {tickets.map(ticket => (
                            <div
                              key={ticket.id}
                              className="amount-list float-start w-100 d-flex mb-1"
                            >
                              <div className="w-100 d-flex justify-content-between">
                                <h3 className="card-title">
                                  {ticket.ticket} x {ticket.count}
                                </h3>
                                <h3 className="card-title">₹ {ticket.cost}</h3>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div id="ticket-totals">
                      {appliedCoupon && (
                        <div className="grand-total float-start w-100 ">
                          <div className="float-start">
                            <h3 className="font-weight-bold font-size-16 text-success mt-1">
                              Discount Applied ({appliedCoupon.discount_percent}
                              %)
                            </h3>
                          </div>
                          <div className="float-end">
                            <p className="text-success">
                              - ₹ {calculations.discountAmount.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      )}
                      <div className="grand-total float-start w-100 mt-2">
                        <div className="border-bottom mb-3" />
                        <div className="float-start">
                          <h3 className="font-weight-bold font-size-16">
                            Total
                          </h3>
                        </div>
                        <div className="float-end">
                          <p>₹ {calculations.subtotal.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="grand-total float-start w-100 mt-1 ">
                        <div className="float-start">
                          <h3 className="font-weight-bold font-size-16">
                            GST (18%)
                          </h3>
                        </div>
                        <div className="float-end">
                          <p>₹ {calculations.gst.toFixed(2)}</p>
                        </div>
                      </div>

                      <div className="grand-total float-start w-100 mt-1">
                        <div className="float-start">
                          <h3 className="h4 font-weight-bold">
                            Payable Amount
                          </h3>
                        </div>
                        <div className="float-end">
                          <p>
                            <b>₹ {calculations.payableAmount.toFixed(2)}</b>
                          </p>
                        </div>
                      </div>
                      <div className="coupon-wrapper mt-1 border-bottom">
                        <div className="mb-1" onClick={toggleCoupon}>
                          <p className="mb-0">Have a coupon code?</p>
                        </div>
                        <div
                          className={`coupon-box mt-2 ${
                            showCoupon ? "show" : ""
                          }`}
                        >
                          <Input
                            type="text"
                            placeholder="Enter coupon code"
                            onChange={e =>
                              setCouponCode(e.target.value.toUpperCase())
                            }
                            value={couponCode}
                          />
                          <Button
                            className="btn-primary bg-primary btn text-white"
                            onClick={() => {
                              appliedCoupon ? removeCoupon() : applyCoupon()
                            }}
                          >
                            {appliedCoupon ? "Remove" : "Apply"}
                          </Button>
                        </div>
                        {appliedCouponError && (
                          <div
                            style={{
                              color: "red",
                              fontSize: "0.8rem",
                              marginTop: "0",
                              marginBottom: "0.75rem",
                              lineHeight: "1.2",
                            }}
                          >
                            {appliedCouponError}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="clearfix"></div>
                    <p>
                      The payment amount is non-refundable. By proceeding, you
                      confirm your acceptance of our Terms and Conditions
                    </p>
                    <button
                      type="submit"
                      className="btn btn-primary w-100 mt-3"
                      disabled={validation.isSubmitting}
                    >
                      {validation.isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Please wait...
                        </>
                      ) : (
                        "BOOK NOW"
                      )}
                    </button>
                  </div>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
        <ErrorPopup
          isOpen={isOpen}
          toggle={() => setIsOpen(!isOpen)}
          message={errorMessage}
        />
        <PageLoader
          open={
            createEventBookingOrderScreen.loading ||
            verifyEventPaymentScreen.loading
          }
        />
      </section>
    </>
  )
}

export default EventAnniversary
